import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { navigate } from 'gatsby'
import type { Product } from 'src/components/restructure/product/types/product'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import { useMobile } from 'src/hooks/useMobile'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'

import { useSkuSelectionModal } from '../../../ProductCard/hooks/useSkuSelectionModal'
import type { AddToCartFn } from '../../../ProductCard/hooks/useAddToCartLogic'
import type { Item } from '../ProductAction/BuyButton/hooks/useBuyButton'

type CrossSellCardContextProps = {
  includeCrossSellProductToCart: Product | undefined
  handleToggle: (checked: boolean, item: Product | undefined) => void
  crossSellCartItem: Item | undefined
  openSkuSelectionModal: (mainItemId?: string) => void
}

const CrossSellCardContext = createContext<CrossSellCardContextProps>(
  {} as CrossSellCardContextProps
)

type CrossSellCardProviderProps = {
  children: React.ReactNode
}

export function CrossSellCardProvider({
  children,
}: CrossSellCardProviderProps) {
  const [includeCrossSellProductToCart, setIncludeCrossSellProductToCart] =
    useState<Product | undefined>(undefined)

  const firstAvailable = includeCrossSellProductToCart?.items?.find(
    (item) => item.isAvailable
  ) as ProductItem | undefined

  const { isMobile } = useMobile()
  const { isLoading, addItems } = useCheckout()
  const { pageData } = useGlobalContext()
  const { openCartFeedback } = useCartFeedback()
  const { closeModal } = useModalSheet()
  const pendingMainItemIdRef = useRef<string | undefined>(undefined)

  const addToCartFn: AddToCartFn = useCallback(
    async (type, sku) => {
      const standardObject = getStandardObject(pageData)
      const price =
        sku.sellerDefault?.sellerOffer?.cashPrice ??
        sku.sellerDefault?.sellerOffer?.price ??
        0

      const productDetail =
        sku.analytics?.amplitude &&
        getProductQueryObject({
          ...sku.analytics.amplitude,
          quantity: 1,
          purchase_price: price,
          purchase_value: price,
        })

      if (productDetail) {
        dispatchAmplitudeEvent({
          eventName: 'Product Added To Cart',
          eventData: {
            ...standardObject,
            'product details': [productDetail],
            'add location': pageData.currentPageType,
            'product viewed source': 'add_together',
            'add to cart source': 'add_together',
          },
        })
      }

      const item = {
        id: sku.itemId,
        productId: sku.productId,
        seller: sku.sellerDefault?.sellerId ?? '1',
        quantity: 1,
        index: 0,
      }

      await addItems([item])
      closeModal()

      const mainItemId = pendingMainItemIdRef.current
      const feedbackIds = mainItemId ? [mainItemId, item.id] : [item.id]

      pendingMainItemIdRef.current = undefined

      if (type === 'buyNow') {
        navigate('/checkout/profile')

        return
      }

      openCartFeedback(feedbackIds)
    },
    [addItems, closeModal, openCartFeedback, pageData]
  )

  const { openSkuSelectionModal: openModal } = useSkuSelectionModal({
    product: includeCrossSellProductToCart as Product,
    currentSku: firstAvailable as ProductItem,
    Images: firstAvailable?.images ?? [],
    isLoading,
    addToCartFn,
    isMobile,
    standardObject: null,
    productObject: null,
  })

  const openSkuSelectionModal = useCallback(
    (mainItemId?: string) => {
      pendingMainItemIdRef.current = mainItemId
      openModal()
    },
    [openModal]
  )

  const crossSellCartItem: Item | undefined = useMemo(() => {
    if (!includeCrossSellProductToCart || !firstAvailable) {
      return undefined
    }

    const hasMultipleItems = includeCrossSellProductToCart.items.length > 1

    if (hasMultipleItems) {
      return undefined
    }

    return {
      id: firstAvailable.itemId,
      productId: firstAvailable.productId,
      seller: firstAvailable.sellerDefault?.sellerId ?? '1',
      quantity: 1,
      index: 0,
      price: firstAvailable.sellerDefault?.sellerOffer?.cashPrice ?? 0,
      amplitude: firstAvailable.analytics?.amplitude,
    }
  }, [firstAvailable, includeCrossSellProductToCart])

  const handleToggle = useCallback(
    (checked: boolean, item: Product | undefined) => {
      setIncludeCrossSellProductToCart(checked ? item : undefined)
    },
    []
  )

  const value = useMemo(
    () => ({
      includeCrossSellProductToCart,
      handleToggle,
      crossSellCartItem,
      openSkuSelectionModal,
    }),
    [
      includeCrossSellProductToCart,
      handleToggle,
      crossSellCartItem,
      openSkuSelectionModal,
    ]
  )

  return (
    <CrossSellCardContext.Provider value={value}>
      {children}
    </CrossSellCardContext.Provider>
  )
}

export const useCrossSellCardContext = () => useContext(CrossSellCardContext)
